import { Box, Modal, SxProps } from "@mui/material";
import React, { createContext, useContext, useState } from "react";
import { useTranslation } from "react-i18next";

type ModalContextType = {
    openModal: (content: React.ReactElement, sizePreset?: ModalPredefinedSize) => void;
    closeModal: () => void
};

/**
 * Enum of predetermined sizes. default is flexible and
 * takes up space enough to display its contents
 */
export enum ModalPredefinedSize {
    default = "default",
    small = "small",
    medium = "medium",
    large = "large",
    fullScreen = "fullScreen"
}

const ModalContext = createContext<ModalContextType | null>(null);

/**
 * Base component. Takes some root react element and displays it in a modal form
 * hovering above the calling UI
 */
export function ModalProvider({ children, sizePreset }:
    {
        children: React.ReactElement
        sizePreset?: ModalPredefinedSize
    }) {
    const [isOpened, setIsOpened] = useState(false);
    const [modalContents, setModalContents] = useState<React.ReactElement>((<></>));
    const [modalSize, setModalSize] = useState<ModalPredefinedSize>(sizePreset ?? ModalPredefinedSize.default);

    const openModal = (content: React.ReactElement, openSizePreset?: ModalPredefinedSize) => {
        setModalContents(content);
        setModalSize(openSizePreset ?? sizePreset ?? ModalPredefinedSize.default);
        setIsOpened(true);
    };

    const closeModal = () => {
        setIsOpened(false);
    };

    // Modal closed by clicking outside of it
    const handleModalClosed = () => {
        setIsOpened(false);
    };

    const defaultStyle: SxProps = {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        bgcolor: "background.paper",
        boxShadow: 24,
        borderRadius: 2,
        overflow: "hidden"
    };
    const smallStyle: SxProps = {
        position: "absolute",
        top: "50%",
        left: "50%",
        maxWidth: "25%",
        minWidth: "25%",
        transform: "translate(-50%, -50%)",
        bgcolor: "background.paper",
        boxShadow: 24,
        borderRadius: 2,
        overflow: "hidden"
    };
    const mediumStyle: SxProps = {
        position: "absolute",
        top: "50%",
        left: "50%",
        maxWidth: "50%",
        minWidth: "50%",
        transform: "translate(-50%, -50%)",
        bgcolor: "background.paper",
        boxShadow: 24,
        borderRadius: 2,
        overflow: "hidden"
    };
    const largeStyle: SxProps = {
        position: "absolute",
        top: "50%",
        left: "50%",
        maxWidth: "75%",
        minWidth: "75%",
        transform: "translate(-50%, -50%)",
        bgcolor: "background.paper",
        boxShadow: 24,
        borderRadius: 2,
        overflow: "hidden"
    };
    const fullscreenStyle: SxProps = {
        position: "absolute",
        top: "50%",
        left: "50%",
        maxWidth: "100%",
        minWidth: "100%",
        maxHeight: "100%",
        minHeight: "100%",
        transform: "translate(-50%, -50%)",
        bgcolor: "background.paper",
        boxShadow: 24,
        borderRadius: 2,
        overflow: "hidden"
    };

    const selectedStyle = (() => {
        switch (modalSize) {
            case ModalPredefinedSize.small: return smallStyle;
            case ModalPredefinedSize.medium: return mediumStyle;
            case ModalPredefinedSize.large: return largeStyle;
            case ModalPredefinedSize.fullScreen: return fullscreenStyle;
            default: return defaultStyle;
        }
    })();

    return (
        <ModalContext.Provider value={{ openModal, closeModal }}>
            {children}
            <Modal
                open={isOpened}
                onClose={handleModalClosed}>
                <Box sx={selectedStyle}>
                    {modalContents}
                </Box>
            </Modal>
        </ ModalContext.Provider>
    );
}

/**
 * Hooks providing the modal context with the functions for interacting
 * with the modal form
 */
export function useModal(): ModalContextType {
    const c = useContext(ModalContext);
    if (!c) throw new Error("useModal must be used inside a ModalProvider");
    return c;
}

// -----------------------------------------------------------------------------
// Predefined modal forms
// -----------------------------------------------------------------------------
/**
 * Template for a small modal form that displays a message and prompts the user
 * to either accept or reject, then returns a boolean value based on the user's
 * choice. True = accept, false = reject
 * Usage:
 * openModal(<ConfirmationModal
 *              header="header"
 *              message="message"
 *              onModalResponse={(r: bool) => { console.log("This was the response: ", r) }} />)
 */
export function ConfirmationModal({ header, message, onModalResponse }:
    {
        header: string,
        message: string
        onModalResponse: (response: boolean) => void
    }) {

    const { t } = useTranslation();
    const { closeModal } = useModal();

    const selectResponse = (r: boolean) => {
        onModalResponse(r);
        closeModal();
    };

    return (
        <>
            <div className="modal-header" style={{ backgroundColor: "#3a3f51" }}>
                <h3 className="modal-title">{header}</h3>
            </div>
            <div className="modal-body">
                {message}
            </div>
            <div className="modal-footer">
                <button className="btn btn-primary" onClick={() => selectResponse(true)}>{t("common.yes")}</button>
                <button className="btn btn-default" onClick={() => selectResponse(false)}>{t("common.cancel")}</button>
            </div>
        </>
    );
}
